import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Patient.module.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
    FaUsers, 
    FaUserPlus, 
    FaFileImport, 
    FaFileExport, 
    FaProcedures, 
    FaCalendarAlt, 
    FaAmbulance,
    FaSearch, 
    FaFilter, 
    FaRedoAlt,
    FaEllipsisV, 
    FaEye, 
    FaEdit, 
    FaMars, 
    FaVenus,
    FaSpinner
} from "react-icons/fa";
import { useState, useEffect, useMemo } from "react";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENTS ---
const SkeletonStatsCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonIcon}></div>
        <div className={classes.skeletonCardInfo}>
            <div className={classes.skeletonText}></div>
            <div className={classes.skeletonText} style={{ width: '60%' }}></div>
            <div className={classes.skeletonText} style={{ width: '40%' }}></div>
        </div>
    </div>
);

const SkeletonRow = () => (
    <tr className={classes.skeletonRow}>
        <td>
            <div className={classes.skeletonPatientInfo}>
                <div className={classes.skeletonAvatar}></div>
                <div>
                    <div className={classes.skeletonText} style={{ width: '120px' }}></div>
                    <div className={classes.skeletonText} style={{ width: '80px' }}></div>
                </div>
            </div>
        </td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonBadge}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td>
            <div className={classes.skeletonActions}>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
            </div>
        </td>
    </tr>
);

function Patient() {
    const navigate = useNavigate();
    
    // State for patients data
    const [patients, setPatients] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 100,
        total: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [stats, setStats] = useState([
        {
            title: "Total Patients",
            value: "0",
            link: "View all patients",
            icon: <FaUsers />,
            color: "#2F80ED",
            bg: "#EDF5FF",
        },
        {
            title: "New Patients (This Month)",
            value: "0",
            link: "View new patients",
            icon: <FaUserPlus />,
            color: "#27AE60",
            bg: "#ECFFF5",
        },
        {
            title: "Admitted Patients",
            value: "0",
            link: "View admitted",
            icon: <FaProcedures />,
            color: "#9B51E0",
            bg: "#F7EEFF",
        },
        {
            title: "Outpatients (Today)",
            value: "0",
            link: "View outpatients",
            icon: <FaCalendarAlt />,
            color: "#F2994A",
            bg: "#FFF6EB",
        },
        {
            title: "Emergency (Today)",
            value: "0",
            link: "View emergency",
            icon: <FaAmbulance />,
            color: "#EB5757",
            bg: "#FFF0F0",
        },
    ]);

    const [search, setSearch] = useState("");
    const [department, setDepartment] = useState("");
    const [status, setStatus] = useState("");
    const [bloodGroup, setBloodGroup] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const rowsPerPage = 5;

    // Fetch patients from API
    const fetchPatients = async (page = 1) => {
        setLoading(true);
        setError("");
        try {
            const companyId = localStorage.getItem("companyId") || "1";
            
            const response = await axios.get(`${BASE_URL}/api/patient`, {
                params: {
                    company_id: parseInt(companyId),
                    page: page,
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });

            console.log("Patients API Response:", response.data);

            // Handle the paginated response structure
            if (response.data && response.data.data) {
                const responseData = response.data.data;
                
                // Get the patients array from the nested data
                let patientsData = [];
                if (Array.isArray(responseData.data)) {
                    patientsData = responseData.data;
                } else if (Array.isArray(responseData)) {
                    patientsData = responseData;
                } else {
                    patientsData = [responseData];
                }

                setPatients(patientsData);
                
                // Set pagination info
                setPagination({
                    current_page: responseData.current_page || 1,
                    last_page: responseData.last_page || 1,
                    per_page: responseData.per_page || 100,
                    total: responseData.total || patientsData.length,
                });

                // Update stats based on fetched data
                updateStats(patientsData);
            } else {
                setPatients([]);
                setPagination({
                    current_page: 1,
                    last_page: 1,
                    per_page: 100,
                    total: 0,
                });
            }

        } catch (error) {
            console.error("Error fetching patients:", error);
            let errorMsg = "Failed to load patients. Please try again.";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === 'object') {
                        const firstKey = Object.keys(message)[0];
                        if (firstKey && Array.isArray(message[firstKey])) {
                            errorMsg = message[firstKey][0];
                        } else {
                            errorMsg = JSON.stringify(message);
                        }
                    } else if (typeof message === 'string') {
                        errorMsg = message;
                    }
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Update stats based on patient data
    const updateStats = (patientsData) => {
        const total = patientsData.length || 0;
        
        // Get current month patients
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const newPatients = patientsData.filter(p => {
            if (p.created_at) {
                const date = new Date(p.created_at);
                return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
            }
            return false;
        });

        // Get admitted patients
        const admitted = patientsData.filter(p => 
            p.status?.toLowerCase() === "admitted" || p.status?.toLowerCase() === "inpatient"
        );

        // Get active/outpatients
        const outpatients = patientsData.filter(p => 
            p.status?.toLowerCase() === "active" || p.status?.toLowerCase() === "outpatient"
        );

        // Get emergency patients
        const emergency = patientsData.filter(p => 
            p.department?.toLowerCase() === "emergency" || p.consultation_type?.toLowerCase() === "emergency"
        );

        setStats([
            {
                title: "Total Patients",
                value: total.toString(),
                link: "View all patients",
                icon: <FaUsers />,
                color: "#2F80ED",
                bg: "#EDF5FF",
            },
            {
                title: "New Patients (This Month)",
                value: newPatients.length.toString(),
                link: "View new patients",
                icon: <FaUserPlus />,
                color: "#27AE60",
                bg: "#ECFFF5",
            },
            {
                title: "Admitted Patients",
                value: admitted.length.toString(),
                link: "View admitted",
                icon: <FaProcedures />,
                color: "#9B51E0",
                bg: "#F7EEFF",
            },
            {
                title: "Outpatients (Today)",
                value: outpatients.length.toString(),
                link: "View outpatients",
                icon: <FaCalendarAlt />,
                color: "#F2994A",
                bg: "#FFF6EB",
            },
            {
                title: "Emergency (Today)",
                value: emergency.length.toString(),
                link: "View emergency",
                icon: <FaAmbulance />,
                color: "#EB5757",
                bg: "#FFF0F0",
            },
        ]);
    };

    // Load patients on component mount
    useEffect(() => {
        fetchPatients(currentPage);
    }, []);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Get avatar fallback
    const getAvatar = (patient) => {
        if (patient.avatar) return patient.avatar;
        const gender = patient.gender?.toLowerCase() === "female" ? "women" : "men";
        const id = patient.id || Math.floor(Math.random() * 100);
        return `https://randomuser.me/api/portraits/${gender}/${id % 70 + 1}.jpg`;
    };

    // Get initials for avatar fallback
    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return parts[0][0] + parts[1][0];
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Get full name from patient data
    const getFullName = (patient) => {
        const firstName = patient.first_name || '';
        const middleName = patient.middle_name || '';
        const lastName = patient.last_name || '';
        const name = patient.name || '';
        
        if (firstName && lastName) {
            return `${firstName} ${middleName} ${lastName}`.trim();
        }
        return name || `${firstName} ${lastName}`.trim() || "Unknown";
    };

    // Extract unique departments for filter
    const departments = useMemo(() => {
        const depts = new Set();
        patients.forEach(p => {
            if (p.department) depts.add(p.department);
            if (p.department_name) depts.add(p.department_name);
        });
        return Array.from(depts);
    }, [patients]);

    // Extract unique statuses for filter
    const statuses = useMemo(() => {
        const stat = new Set();
        patients.forEach(p => {
            if (p.status) stat.add(p.status);
        });
        return Array.from(stat);
    }, [patients]);

    // Extract unique blood groups for filter
    const bloodGroups = useMemo(() => {
        const bg = new Set();
        patients.forEach(p => {
            if (p.blood_group) bg.add(p.blood_group);
        });
        return Array.from(bg);
    }, [patients]);

    const filteredPatients = useMemo(() => {
        return patients.filter((patient) => {
            const keyword = search.toLowerCase();
            const fullName = getFullName(patient).toLowerCase();

            const searchMatch =
                patient.first_name?.toLowerCase().includes(keyword) ||
                patient.last_name?.toLowerCase().includes(keyword) ||
                patient.patient_no?.toLowerCase().includes(keyword) ||
                patient.phone?.toLowerCase().includes(keyword) ||
                patient.email?.toLowerCase().includes(keyword) ||
                fullName.includes(keyword);

            const departmentMatch =
                department === "" ||
                patient.department === department ||
                patient.department_name === department;

            const statusMatch =
                status === "" ||
                patient.status === status;

            const bloodMatch =
                bloodGroup === "" ||
                patient.blood_group === bloodGroup;

            return (
                searchMatch &&
                departmentMatch &&
                statusMatch &&
                bloodMatch
            );
        });
    }, [patients, search, department, status, bloodGroup]);

    const totalPages = Math.ceil(filteredPatients.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentPatients = filteredPatients.slice(indexOfFirst, indexOfLast);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleView = (patient) => {
        console.log("Viewing:", patient);
        navigate(`/patient/${patient.id}`);
    };

    const handleEdit = (patient) => {
        console.log("Editing:", patient);
        navigate(`/edit-patient/${patient.id}`);
    };

    const handleMore = (patient) => {
        console.log("More Options:", patient);
    };

    const resetFilters = () => {
        setSearch("");
        setDepartment("");
        setStatus("");
        setBloodGroup("");
        setCurrentPage(1);
    };

    // Render skeleton rows
    const renderSkeletonRows = () => {
        return Array(5).fill(0).map((_, index) => (
            <SkeletonRow key={`skeleton-${index}`} />
        ));
    };

    // Render skeleton stats
    const renderSkeletonStats = () => {
        return Array(5).fill(0).map((_, index) => (
            <SkeletonStatsCard key={`skeleton-stat-${index}`} />
        ));
    };

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.main}>
                <Header />
                <div className={classes.content}>
                    {/* TOP SECTION */}
                    <div className={classes.top}>
                        <div>
                            <h2>Patients</h2>
                            <div className={classes.breadcrumb}>
                                Dashboard
                                <span>&gt;</span>
                                Patients
                            </div>
                        </div>
                        <div className={classes.actions}>
                            <button className={classes.outline}>
                                <FaFileImport />
                                Import Patients
                            </button>
                            <button className={classes.outline}>
                                <FaFileExport />
                                Export
                            </button>
                            <button
                                className={classes.primary}
                                onClick={() => navigate("/addPatientPage")}
                            >
                                + Add New Patient
                            </button>
                        </div>
                    </div>

                    {/* STATS CARDS - with skeleton */}
                    <div className={classes.cards}>
                        {loading ? (
                            renderSkeletonStats()
                        ) : (
                            stats.map((item, index) => (
                                <div className={classes.card} key={index}>
                                    <div
                                        className={classes.icon}
                                        style={{
                                            background: item.bg,
                                            color: item.color
                                        }}
                                    >
                                        {item.icon}
                                    </div>
                                    <div className={classes.cardInfo}>
                                        <p>{item.title}</p>
                                        <h2>{item.value}</h2>
                                        <button>{item.link}</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* SEARCH & FILTERS */}
                    <div className={classes.searchContainer}>
                        <div className={classes.searchBox}>
                            <FaSearch className={classes.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by Name, UHID, Phone, Email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <select
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Departments</option>
                            {departments.map((dept) => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>

                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Status</option>
                            {statuses.map((stat) => (
                                <option key={stat} value={stat}>{stat}</option>
                            ))}
                        </select>

                        <select
                            value={bloodGroup}
                            onChange={(e) => setBloodGroup(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Blood Groups</option>
                            {bloodGroups.map((bg) => (
                                <option key={bg} value={bg}>{bg}</option>
                            ))}
                        </select>

                        <button className={classes.filterBtn} disabled={loading}>
                            <FaFilter />
                            More Filters
                        </button>

                        <button
                            className={classes.resetBtn}
                            onClick={resetFilters}
                            disabled={loading}
                        >
                            <FaRedoAlt />
                            Reset
                        </button>
                    </div>

                    {/* ERROR MESSAGE */}
                    {error && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button onClick={() => fetchPatients(currentPage)} className={classes.retryBtn}>
                                Retry
                            </button>
                        </div>
                    )}

                    {/* TABLE with skeleton */}
                    <div className={classes.tableContainer}>
                        {loading ? (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Patient</th>
                                        <th>UHID</th>
                                        <th>Gender</th>
                                        <th>Age</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Status</th>
                                        <th>Last Visit</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderSkeletonRows()}
                                </tbody>
                            </table>
                        ) : (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Patient</th>
                                        <th>UHID</th>
                                        <th>Gender</th>
                                        <th>Age</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Status</th>
                                        <th>Last Visit</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPatients.length > 0 ? (
                                        currentPatients.map((patient) => {
                                            const fullName = getFullName(patient);
                                            const avatar = getAvatar(patient);
                                            const dob = patient.date_of_birth;
                                            const age = patient.age || (dob ? new Date().getFullYear() - new Date(dob).getFullYear() : "N/A");
                                            const phone = patient.phone || "N/A";
                                            const email = patient.email || "N/A";
                                            const dept = patient.department || patient.department_name || "N/A";
                                            const stat = patient.status || "Active";
                                            const lastVisit = patient.updated_at ? formatDate(patient.updated_at) : "N/A";

                                            return (
                                                <tr key={patient.id}>
                                                    <td>
                                                        <div className={classes.patientInfo}>
                                                            <img
                                                                src={avatar}
                                                                alt={fullName}
                                                                className={classes.avatar}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                            <div className={classes.avatarFallback} style={{ display: 'none' }}>
                                                                {getInitials(fullName)}
                                                            </div>
                                                            <div>
                                                                <h4>{fullName}</h4>
                                                                <span>DOB : {formatDate(dob)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{patient.patient_no || "N/A"}</td>
                                                    <td>
                                                        <div className={classes.gender}>
                                                            {patient.gender?.toLowerCase() === "male" ? (
                                                                <>
                                                                    <FaMars className={classes.male} />
                                                                    Male
                                                                </>
                                                            ) : patient.gender?.toLowerCase() === "female" ? (
                                                                <>
                                                                    <FaVenus className={classes.female} />
                                                                    Female
                                                                </>
                                                            ) : (
                                                                patient.gender || "N/A"
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{age}</td>
                                                    <td>{phone}</td>
                                                    <td>{email}</td>
                                                    <td>{dept}</td>
                                                    <td>
                                                        <span
                                                            className={`${classes.badge}
                                                            ${stat.toLowerCase() === "active" || stat.toLowerCase() === "outpatient"
                                                                ? classes.active
                                                                : stat.toLowerCase() === "admitted" || stat.toLowerCase() === "inpatient"
                                                                ? classes.admitted
                                                                : classes.discharged
                                                            }`}
                                                        >
                                                            {stat}
                                                        </span>
                                                    </td>
                                                    <td>{lastVisit}</td>
                                                    <td>
                                                        <div className={classes.tableActions}>
                                                            <button
                                                                onClick={() => handleView(patient)}
                                                                className={classes.viewBtn}
                                                                title="View Patient"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(patient)}
                                                                className={classes.editBtn}
                                                                title="Edit Patient"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                onClick={() => handleMore(patient)}
                                                                className={classes.moreBtn}
                                                                title="More Options"
                                                            >
                                                                <FaEllipsisV />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="10" style={{ textAlign: "center", padding: "40px" }}>
                                                No Patients Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* PAGINATION */}
                    {!loading && filteredPatients.length > 0 && (
                        <div className={classes.pagination}>
                            <p>
                                Showing
                                <strong>
                                    {" "}
                                    {filteredPatients.length > 0 ? indexOfFirst + 1 : 0}
                                    -
                                    {Math.min(indexOfLast, filteredPatients.length)}
                                </strong>
                                {" "}of{" "}
                                <strong>{filteredPatients.length}</strong>
                                patients
                            </p>
                            <div className={classes.pages}>
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goToPage(i + 1)}
                                        className={currentPage === i + 1 ? classes.activePage : ""}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Patient;